/**
 * Kehitys — the feedback and development board.
 *
 * Default order is most votes first, because that ordering is the entire point
 * of the page.
 */

import { useState } from "react";
import { toast } from "sonner";

import { FeedbackDetail } from "@/components/kehitys/FeedbackDetail";
import { FeedbackRow } from "@/components/kehitys/FeedbackRow";
import { NewReportDialog } from "@/components/kehitys/NewReportDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  FEEDBACK_SORT_OPTIONS,
  FEEDBACK_STATUS_FILTERS,
  type FeedbackSort,
} from "@/lib/feedbackMeta";
import { AddIcon, SearchIcon } from "@/lib/icons";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useFeedbackReports } from "@/hooks/useFeedbackReports";
import type { FeedbackKind, FeedbackReport } from "@/types";

/** Radix Tabs and Select both reject an empty string as an item value, so the
 *  two "no filter" options travel through the controls under sentinels. */
const KIND_ALL = "ALL";
const STATUS_OPEN = "OPEN";

const KIND_TABS: { value: string; label: string }[] = [
  { value: KIND_ALL, label: "Kaikki" },
  { value: "BUG", label: "Viat" },
  { value: "IDEA", label: "Ideat" },
];

export function Kehitys() {
  const [kind, setKind] = useState<FeedbackKind | null>(null);
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState<FeedbackSort>("votes");
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput, 300);
  const [openReportId, setOpenReportId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<FeedbackReport | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { reports, loading, error, toggleVote, createReport, deleteReport } =
    useFeedbackReports({ kind, status, sort, search });

  const handleToggleVote = async (reportId: string) => {
    const result = await toggleVote(reportId);
    if (result && !result.success) {
      toast.error(result.message ?? "Äänestys epäonnistui");
    }
  };

  const requestDelete = (report: FeedbackReport, event: React.MouseEvent) => {
    // The row opens the report on click, so the trash icon must not bubble.
    event.stopPropagation();
    setReportToDelete(report);
  };

  const confirmDelete = async () => {
    if (!reportToDelete) return;
    setDeleting(true);
    try {
      const result = await deleteReport(reportToDelete.id);
      if (result?.success) {
        toast.success(result.message ?? "Ilmoitus poistettu");
        setReportToDelete(null);
      } else {
        toast.error(result?.message ?? "Ilmoituksen poisto epäonnistui");
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="sticky top-0 z-20 flex flex-wrap items-center gap-3 px-4 py-3 border-b bg-background shrink-0 -mx-6 -mt-6">
        <h1 className="text-lg font-semibold mr-2">Kehitys</h1>

        <Tabs
          value={kind ?? KIND_ALL}
          onValueChange={(value) =>
            setKind(value === KIND_ALL ? null : (value as FeedbackKind))
          }
          className="w-auto"
        >
          <TabsList className="grid w-[210px] grid-cols-3">
            {KIND_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <Select
          value={status === "" ? STATUS_OPEN : status}
          onValueChange={(value) => setStatus(value === STATUS_OPEN ? "" : value)}
        >
          <SelectTrigger className="w-[150px]" aria-label="Tila">
            <SelectValue placeholder="Tila" />
          </SelectTrigger>
          <SelectContent>
            {FEEDBACK_STATUS_FILTERS.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value === "" ? STATUS_OPEN : option.value}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={sort}
          onValueChange={(value) => setSort(value as FeedbackSort)}
        >
          <SelectTrigger className="w-[180px]" aria-label="Järjestys">
            <SelectValue placeholder="Järjestys" />
          </SelectTrigger>
          <SelectContent>
            {FEEDBACK_SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Hae ilmoituksista"
            placeholder="Hae ilmoituksia..."
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            className="pl-9"
          />
        </div>

        <Button onClick={() => setDialogOpen(true)} size="sm" className="ml-auto">
          <AddIcon className="mr-2 h-4 w-4" />
          Uusi ilmoitus
        </Button>
      </div>

      <div className="flex flex-col gap-2 pt-3">
        {error && (
          <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
            Ilmoituksia ei voitu ladata. Päivitä sivu ja yritä uudelleen.
          </p>
        )}

        {loading && reports.length === 0 ? (
          [0, 1, 2, 3].map((index) => (
            <Skeleton key={index} className="h-[70px] w-full rounded-lg" />
          ))
        ) : reports.length === 0 ? (
          <div className="rounded-lg border border-dashed px-6 py-14 text-center">
            <p className="text-sm font-semibold">Ei ilmoituksia</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Huomasitko vian tai puuttuvan toiminnon? Kerro siitä — kuvakaappaus
              riittää usein selityksen sijaan.
            </p>
            <Button className="mt-4" onClick={() => setDialogOpen(true)}>
              Tee ensimmäinen ilmoitus
            </Button>
          </div>
        ) : (
          reports.map((report) => (
            <FeedbackRow
              key={report.id}
              report={report}
              onOpen={setOpenReportId}
              onToggleVote={handleToggleVote}
              canDelete={report.viewerCanDelete}
              onRequestDelete={requestDelete}
            />
          ))
        )}
      </div>

      <NewReportDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreate={createReport}
      />

      <FeedbackDetail
        reportId={openReportId}
        onClose={() => setOpenReportId(null)}
        onToggleVote={handleToggleVote}
      />

      {/* One controlled dialog for the whole list rather than one per row, the
          same shape Hankkeet uses. The count of votes and comments is spelled
          out because those are other people's contributions, not just the
          reporter's. */}
      <AlertDialog
        open={reportToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setReportToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Poista ilmoitus</AlertDialogTitle>
            <AlertDialogDescription>
              {reportToDelete && (
                <>
                  <strong>{reportToDelete.reference}</strong> — {reportToDelete.title}
                  <br />
                  Ilmoitus katoaa listalta kaikilta, myös sen{" "}
                  {reportToDelete.voteCount} äänen ja {reportToDelete.commentCount}{" "}
                  kommentin osalta. Käyttöliittymästä sitä ei saa takaisin.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Peruuta</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(event) => {
                // Keep the dialog mounted until the mutation resolves, so a
                // failure can surface instead of the dialog vanishing.
                event.preventDefault();
                void confirmDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Poistetaan…" : "Poista"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
